// Main-thread facade for the ML worker, with observable status for the UI.

import { estimateMs, recordMs } from '../eta';
import type { DepthData, RgbaData } from '../types';
import { DEFAULT_WEB_MODEL, type Backend } from './models';
import type { DistributiveOmit, MlReply, MlRequest, SegmentPoint } from './protocol';
import MlWorker from './ml.worker.ts?worker';

export const mlStatus = $state({
  phase: 'idle' as 'idle' | 'download' | 'init' | 'infer' | 'ready' | 'error',
  loaded: 0,
  total: 0,
  backend: null as Backend | null,
  error: '',
  lastMs: 0,
});

let worker: Worker | null = null;
let nextId = 1;
const waiting = new Map<number, { resolve: (r: MlReply) => void; reject: (e: Error) => void }>();
let queue: Promise<unknown> = Promise.resolve();

function getWorker(): Worker {
  if (!worker) {
    worker = new MlWorker();
    worker.onmessage = (ev: MessageEvent<MlReply>) => {
      const r = ev.data;
      if (r.type === 'progress') {
        mlStatus.phase = r.phase;
        mlStatus.loaded = r.loaded;
        mlStatus.total = r.total;
        return;
      }
      const w = r.id !== undefined ? waiting.get(r.id) : undefined;
      if (!w) return;
      waiting.delete(r.id!);
      if (r.type === 'error') w.reject(new Error(r.message));
      else w.resolve(r);
    };
  }
  return worker;
}

function send(req: DistributiveOmit<MlRequest, 'id'>, transfer: Transferable[] = []): Promise<MlReply> {
  const id = nextId++;
  return new Promise((resolve, reject) => {
    waiting.set(id, { resolve, reject });
    getWorker().postMessage({ ...req, id } as MlRequest, transfer);
  });
}

/** Serialises calls: one model session, one inference at a time. */
function serial<T>(fn: () => Promise<T>): Promise<T> {
  const p = queue.then(fn, fn);
  queue = p.catch(() => {});
  return p;
}

export function mlEstimateMs(model = DEFAULT_WEB_MODEL): number {
  return estimateMs(`ml:${model}:${mlStatus.backend ?? 'wasm'}`, 1);
}

export function estimateDepth(
  img: RgbaData,
  model = DEFAULT_WEB_MODEL,
  backend: Backend | 'auto' = 'auto',
): Promise<DepthData> {
  return serial(async () => {
    mlStatus.error = '';
    try {
      // Copy: the caller keeps its image.
      const rgba = new Uint8ClampedArray(img.data);
      const pending = send({ type: 'infer', model, backend, rgba, width: img.width, height: img.height }, [
        rgba.buffer,
      ]);
      // Phase becomes 'infer' once loading has finished (progress events stop).
      const r = await pending;
      if (r.type !== 'result') throw new Error('unexpected reply');
      mlStatus.backend = r.backend;
      mlStatus.phase = 'ready';
      mlStatus.lastMs = r.ms;
      recordMs(`ml:${model}:${r.backend}`, 1, r.ms);
      return { width: r.width, height: r.height, data: r.data };
    } catch (e) {
      mlStatus.phase = 'error';
      mlStatus.error = e instanceof Error ? e.message : String(e);
      throw e;
    }
  });
}

export function preloadModel(model = DEFAULT_WEB_MODEL, backend: Backend | 'auto' = 'auto'): Promise<Backend> {
  return serial(async () => {
    const r = await send({ type: 'load', model, backend });
    if (r.type !== 'loaded') throw new Error('unexpected reply');
    mlStatus.backend = r.backend;
    mlStatus.phase = 'ready';
    return r.backend;
  });
}

let segmentedKey = '';

/**
 * Object mask (255 = inside) for prompt points on `img` (image coordinates).
 * The image embedding is computed once per `imageKey`.
 */
export function segmentImage(
  img: RgbaData,
  imageKey: string,
  points: SegmentPoint[],
): Promise<{ mask: Uint8Array; score: number }> {
  return serial(async () => {
    const fresh = segmentedKey !== imageKey;
    const rgba = fresh ? new Uint8ClampedArray(img.data) : undefined;
    const r = await send(
      { type: 'segment', imageKey, rgba, width: img.width, height: img.height, points },
      rgba ? [rgba.buffer] : [],
    );
    if (r.type !== 'mask') throw new Error('unexpected reply');
    segmentedKey = imageKey;
    mlStatus.phase = 'ready';
    return { mask: r.mask, score: r.score };
  });
}
